import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Layout } from '@/components/Layout/Layout';
import { Main } from '@/components/Main/Main';
import { Section } from '@/components/Section/Section';
// Components
import { Context } from '@/components/Store';
import { ActionTypes } from '@/components/Store.d';
// Modules
import { errorMessages, errorPageMessages } from '@/lib/config';
import { log } from '@/lib/log';
import { setInnerHTML } from '@/lib/util';

type ErrorMessage = {
  title: string;
  body: string;
};

const ErrorPage = () => {
  const [state, dispatch] = useContext(Context);
  const [errorMessage, toggleError] = useState<ErrorMessage>({ title: '', body: '' });
  const location = useLocation();

  useEffect(() => {
    void (async () => {
      const { setTitle } = await import('@/lib/util');
      setTitle('Fehlerseite');
    })();

    const sessionState = JSON.parse(sessionStorage.getItem('sessionData') ?? '');
    if (sessionState) {
      dispatch({
        type: ActionTypes.restoreFromStorage,
        payload: sessionState
      });
    }

    dispatch({
      type: ActionTypes.setCurrentPage,
      payload: {
        key: 'currentPage',
        value: 'errorPage'
      }
    });

    dispatch({
      type: ActionTypes.showProductOnRight,
      payload: false
    });

    void handleErrorMessages();
    // eslint-disable-next-line
  }, []);

  /**
   * Returns mapped error message if session has expired
   * @return {Object}
   */
  function getSessionExpiredMessage() {
    const title = errorPageMessages.sessionExpiredUnknownProduct.title;
    const body =
      state && state.bundle && state.entryPage
        ? `
            <p>Sie können die Bestellung erneut starten: <a href="${state.entryPage}" title="${state.bundle.name}">${state.bundle.name}</a></p>
            <p>Alle aktuellen Angebote finden Sie unter <a href="https://abo.derstandard.at" title=""DerStandard.at ABO">abo.derstandard.at</a></p>
        `
        : errorPageMessages.sessionExpiredUnknownProduct.body;

    return { title, body };
  }

  const handleErrorMessages = async (): Promise<void> => {
    const searchParams = new URLSearchParams(location.search);
    const urlParams = Object.fromEntries(searchParams.entries());

    const errorCode = Array.isArray(urlParams?.code) ? urlParams.code[0] : urlParams?.code;

    log('Handling error message', errorCode && errorMessages[errorCode as keyof typeof errorMessages]);

    switch (true) {
      case urlParams.code === '404':
        toggleError(errorPageMessages.pageNotFound);
        break;

      case urlParams.code === '440':
      case urlParams.code === 'authorizationDenied':
      case urlParams.code === 'sepaSessionExpired':
        toggleError(getSessionExpiredMessage());
        break;

      case urlParams.code === 'activeProduct':
        toggleError(errorPageMessages.activeProduct);
        break;

      case urlParams.code === 'paypalCancelled':
        toggleError(errorPageMessages.paypalCancelled);
        break;

      case urlParams.code === 'existingContract':
      case urlParams.code === 'existingService':
      case urlParams.code === 'existingSubscription':
        toggleError(errorPageMessages.existingSubscription);
        break;

      case urlParams.code === 'emailInUse':
        toggleError(errorPageMessages.emailInUse);
        break;

      case Boolean(errorCode && errorMessages[errorCode as keyof typeof errorMessages]):
        if (errorCode) {
          const errorMsg = errorMessages[errorCode as keyof typeof errorMessages];
          const title = 'title' in errorMsg && errorMsg.title ? errorMsg.title : errorPageMessages.default.title;
          const body = 'message' in errorMsg && errorMsg.message ? errorMsg.message : errorPageMessages.default.body;
          toggleError({ title, body });
        }
        break;

      default:
        toggleError(errorPageMessages.default);
    }
  };

  return (
    <Layout className="layout-split">
      <Main>
        <Section className="section-error">
          <h1>{errorMessage.title}</h1>
          <div dangerouslySetInnerHTML={setInnerHTML(errorMessage.body)}></div>
        </Section>
      </Main>
    </Layout>
  );
};

export default ErrorPage;
